import { useCallback, useEffect, useState } from "react";

import {
  getPartenairesKata,
  getPartenairesKataStats,
} from "../../../services/partenairesKataApi";

const DEFAULT_FILTERS = {
  q: "",
  region: "",
  departement: "",
  passage_grade: "",
  kata: "",
  role_recherche: "",
  sort: "newest",
  page: 1,
  limit: 12,
};

const DEFAULT_META = {
  page: 1,
  limit: 12,
  total: 0,
  total_pages: 1,
};

const DEFAULT_STATS = {
  total: 0,
  regions: 0,
  departements: 0,
  recent: 0,
};

const useDebouncedValue = (value, delay = 350) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      window.clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default function usePartenairesKata() {
  const [partenaires, setPartenaires] = useState([]);
  const [departmentCounts, setDepartmentCounts] = useState({});
  const [meta, setMeta] = useState(DEFAULT_META);
  const [stats, setStats] = useState(DEFAULT_STATS);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);

  const debouncedSearch = useDebouncedValue(filters.q);

  const loadPartenaires = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getPartenairesKata({
        ...filters,
        q: debouncedSearch,
      });

      setPartenaires(response.data || []);
      setMeta(response.meta || DEFAULT_META);
      setDepartmentCounts(response.department_counts || {});
    } catch (apiError) {
      setPartenaires([]);
      setDepartmentCounts({});
      setError(
        apiError.message ||
          "Impossible de charger les annonces."
      );
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    filters.region,
    filters.departement,
    filters.passage_grade,
    filters.kata,
    filters.role_recherche,
    filters.sort,
    filters.page,
    filters.limit,
    refreshKey,
  ]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      const response = await getPartenairesKataStats();
      setStats(response.data || DEFAULT_STATS);
    } catch {
      setStats(DEFAULT_STATS);
    } finally {
      setStatsLoading(false);
    }
  }, [refreshKey]);

  useEffect(() => {
    loadPartenaires();
  }, [loadPartenaires]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const setFilter = useCallback((name, value) => {
    setFilters((current) => {
      if (name === "page") {
        return {
          ...current,
          page: Number(value),
        };
      }

      return {
        ...current,
        [name]: value,
        page: 1,
      };
    });
  }, []);

  const setLocationFilters = useCallback(
    ({ region = "", departement = "" }) => {
      setFilters((current) => ({
        ...current,
        region,
        departement,
        page: 1,
      }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const refresh = useCallback(() => {
    setRefreshKey((current) => current + 1);
  }, []);

  return {
    partenaires,
    departmentCounts,
    meta,
    stats,

    filters,
    loading,
    statsLoading,
    error,

    setFilter,
    setLocationFilters,
    resetFilters,
    refresh,
  };
}